import React, { useState, useCallback, useRef, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import "./App.css";

import Frame from "./components/Frame";
import GrainOverlay from "./components/GrainOverlay";
import BootSequence from "./components/BootSequence";
import ChapterNav from "./components/ChapterNav";
import TransitionLoader from "./components/TransitionLoader";
import MobileMenu from "./components/MobileMenu";
import Home from "./components/Home";
import Work from "./components/Work";
import Gallery from "./components/Gallery";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";

/* ── PAGE TRANSITION WRAPPER ── */
function PageWrap({ children }) {
  return (
    <motion.div
      className="page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35, ease: "easeInOut" }}
    >
      <div className="page-inner">
        {children}
        <Footer />
      </div>
    </motion.div>
  );
}

/* ── ROUTE PAGES ── */
function HomePage() {
  return (
    <PageWrap>
      <Home />
    </PageWrap>
  );
}

function WorkPage() {
  return (
    <PageWrap>
      <Work />
    </PageWrap>
  );
}

function GalleryPage() {
  return (
    <PageWrap>
      <Gallery />
    </PageWrap>
  );
}

function ContactPage() {
  return (
    <PageWrap>
      <Contact />
    </PageWrap>
  );
}

/* ── MAIN APP ── */
function getInitialTheme() {
  try {
    const saved = localStorage.getItem("saket-theme");
    if (saved === "light" || saved === "dark") return saved;
  } catch (_) {
    /* storage unavailable — fall through to default */
  }
  return "dark";
}

export default function App() {
  const [bootDone, setBootDone] = useState(false);
  const [transitioning, setTransitioning] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState(getInitialTheme);
  const location = useLocation();
  const prevPath = useRef(location.pathname);

  // Apply theme to <html> + persist + sync meta theme-color
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem("saket-theme", theme);
    } catch (_) {
      /* ignore */
    }
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme === "light" ? "#ffffff" : "#000000");
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  // Detect route changes — show loader & close mobile menu
  useEffect(() => {
    if (prevPath.current !== location.pathname) {
      prevPath.current = location.pathname;
      setTransitioning(location.pathname);
      setMenuOpen(false);
    }
  }, [location.pathname]);

  const handleBootDone = useCallback(() => {
    setBootDone(true);
  }, []);

  const handleTransitionDone = useCallback(() => {
    setTransitioning(null);
  }, []);

  return (
    <>
      <GrainOverlay />
      {bootDone && <ScrollToTop />}

      {/* Boot sequence — gates the content */}
      <AnimatePresence>
        {!bootDone && <BootSequence key="boot" onDone={handleBootDone} />}
      </AnimatePresence>

      {/* Premium transition loader on route changes */}
      <AnimatePresence>
        {bootDone && transitioning && (
          <TransitionLoader
            key={transitioning}
            pathname={transitioning}
            onDone={handleTransitionDone}
          />
        )}
      </AnimatePresence>

      {/* Mobile menu — rendered OUTSIDE Frame to avoid overflow clipping */}
      <MobileMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        theme={theme}
        onToggleTheme={toggleTheme}
      />

      {/* Frame wraps everything — clips content inside the viewfinder border */}
      <Frame
        menuOpen={menuOpen}
        onToggleMenu={() => setMenuOpen((v) => !v)}
        onCloseMenu={() => setMenuOpen(false)}
        page={location.pathname === "/" ? "home" : ""}
        theme={theme}
        onToggleTheme={toggleTheme}
      >
        {bootDone && <ChapterNav />}

        {/* Pages with route transitions */}
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<HomePage />} />
            <Route path="/work" element={<WorkPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="*" element={<HomePage />} />
          </Routes>
        </AnimatePresence>
      </Frame>
    </>
  );
}
